/* eslint-disable @typescript-eslint/naming-convention */
import { getServerDate } from "../utils/storage/date";
import type { IHashMap, IModifiedType } from "../sync/sync.types";
import { extend } from "../utils/vanilla";
import { toDateTime } from "../utils/date";
import { SYSTEM_STATUSES, type TDeferralStatus, type THosMalfunction, type THosOrigin, type THosStatus } from "hos-tools";

/* Logs summary:
*   - ordinary status logs - loaded from server or created locally
*   - control logs (ignition, connected, etc) - loaded from server or created locally
*   - placeholders ("continued from previous day") - created during sync process
* */

// Groups HOS Statuses
export const EMERGENCY_RESPONSE_EXEMPTION_LOG_TYPES: THosStatus[] = ["EmergencyResponseExemptionEnabled", "EmergencyResponseExemptionDisabled"];
const CONTROL_LOG_BADGES: Partial<Record<THosStatus, string>> = {
    EmergencyResponseExemptionEnabled: "ERE",
    EmergencyResponseExemptionDisabled: "ERD"
};
export const EXEMPTION_LOG_TYPES: THosStatus[] = ["AdverseWeather", "Exemption16H", "ExemptionOffDutyDeferral", "RailroadExemption", "ExemptionReducedRest",
    ...EMERGENCY_RESPONSE_EXEMPTION_LOG_TYPES];

export const HOS_STANDARD_STATUSES: THosStatus[] = ["ON", "OFF", "D", "SB"];
export const HOS_ADDITIONAL_STATUSES: THosStatus[] = ["PC", "PC_Exempted", "YM", "WT"];

export const ALL_HOS_STATUSES = HOS_STANDARD_STATUSES.concat(HOS_ADDITIONAL_STATUSES);
export const EWD_STANDARD_STATUSES: THosStatus[] = ["Work", "Rest"];
export const EWD_ADDITIONAL_STATUSES: THosStatus[] = ["WorkExemption"];
export const ALL_EWD_STATUSES = EWD_STANDARD_STATUSES.concat(EWD_ADDITIONAL_STATUSES);
export const STANDARD_STATUSES = ALL_HOS_STATUSES.concat(ALL_EWD_STATUSES);
export const INTERMEDIATE_STATUSES: THosStatus[] = ["INT_PC", "INT_D", "INT_CoDriver"];
export const ALL_STANDARD_STATUSES = STANDARD_STATUSES.concat(INTERMEDIATE_STATUSES);
export const MALFUNCTION_STATUSES: THosStatus[] = ["PowerCompliance", "EngineSyncCompliance", "TimingCompliance", "PositioningCompliance",
"DataRecordingCompliance", "DataTransferCompliance", "OtherCompliance", "MissingElementCompliance", "UnidentifiedDrivingCompliance"];
export const CONTROL_STATUSES: THosStatus[] = ["AdverseWeather", "Exemption16H", "ExemptionOffDutyDeferral", "Login", "Logoff",
"Certify", "EnginePowerup", "EngineShutdown", "EnginePowerupPC", "EngineShutdownPC", "Authority", "SituationalDrivingClear", "CanadaCycleOne",
"CanadaCycleTwo", "OperatingZoneCanadaSouthOf60", "OperatingZoneCanadaNorthOf60", "OperatingZoneAmerica", "RailroadExemption", "ExemptionReducedRest"];
export const ENGINE_STATUSES = ["EnginePowerup", "EngineShutdown", "EnginePowerupPC", "EngineShutdownPC"];
export const DISPLAY_STATUSES: THosStatus[] = ["D", "ON", "OFF", "SB", "PC", "PC_Exempted", "YM", "WT", "AdverseWeather", "Exemption16H",
                                               "ExemptionOffDutyDeferral", "Login", "Logoff", "Certify", "Work", "Rest", "WorkExemption",
                                               "RailroadExemption", "ExemptionReducedRest", "EmergencyResponseExemptionEnabled",
                                               "EmergencyResponseExemptionDisabled"];
export const CYCLE_STATUSES = ["CanadaCycleOne", "CanadaCycleTwo"];
export const OPERATING_ZONE_STATUSES = ["OperatingZoneCanadaSouthOf60", "OperatingZoneCanadaNorthOf60", "OperatingZoneAmerica"];
export const SITUATIONAL_DRIVING_CLEAR_STATUS: THosStatus[] = ["SituationalDrivingClear"];
export const DRIVING_IN_EXEMPTION_CONTROL_STATUSES: THosStatus[] = ["SituationalDrivingClear", "DrivingWhileInExemption", "DrivingStoppedWhileInExemption"];
export const CYCLE_AND_OPERATING_ZONE_STATUSES = CYCLE_STATUSES.concat(OPERATING_ZONE_STATUSES);
export const REQUESTED = "Requested";
export const NOUSERID = "NoUserId";
export const NODEVICEID = "NoDeviceId";
// Lives here with its siblings rather than in DVIRLog.ts, which cannot be value-imported by
// model/Image.ts: DVIRLog -> app.view -> login -> model/Image is a cycle.
export const NOTRAILERID = "NoTrailerId";
export const AUTO_ANNOTATION_LABEL = "Auto Annotation";

export interface IAnnotation extends IHashMap<IAnnotation> {
    dateTime: string | Date;
    driver: IIdEntity | "NoUserId";
    comment: string;
    dutyStatusLog?: IIdEntity;
    version?: string;
    isAutoAnnotation?: boolean;
}

export interface IAddress {
    city?: string;
    stateProvince?: string;
    formattedAddress?: string;
    formattedHosAddress?: string;
    country?: string;
}

export interface IDutyStatusLog extends IDeviceEntity, IHashMap<IDutyStatusLog> {
    dateTime: string;
    driver: IIdEntity;
    status: THosStatus;
    origin: THosOrigin;
    annotations?: IAnnotation[];
    coDrivers?: IIdEntity[];
    editDateTime?: string;
    verifyDateTime?: string;
    version?: string;
    location?: IAddressLookupData;
    parentId?: string;
    state?: string;
    sequence?: string;
    malfunction?: THosMalfunction;
    isIgnored?: boolean;
    isHidden?: boolean;
    isRequestedRemoval?: boolean;
    odometer?: number;
    engineHours?: number;
    distanceSinceValidCoordinates?: number;
    deferralStatus?: TDeferralStatus;
    deferralMinutes?: number;
    eventCheckSum?: string;
    userHosRuleSet?: IIdEntity;

    // client-side props
    placeholder?: boolean;
    distance?: number;
    requestedEdit?: IDutyStatusLog;
    requestedAdd?: boolean;
    originalDriverBeforeEdit?: string;
    _isTempIoxLog?: boolean;
    _createdDate?: string;
    _address?: string;
    _customExemptHOSAbbreviation?: string;
    _awaitingTransition?: boolean;
    _originalDateTimeBeforeEdit?: string;
    _originalDistance?: number;
    _nextLogDateTime?: string;
}

interface IComparators {
    default: (property: string) => (newValue: string) => boolean;
    device: (newValue: string | IIdEntity) => boolean;
    driver: (newValue: string | IIdEntity) => boolean;
}

export type IDutyStatusLogType = keyof DutyStatusLog;

export default class DutyStatusLog implements IDutyStatusLog {

    id: string;
    dateTime: string;
    driver: IIdEntity;
    device: IIdEntity;
    status: THosStatus;
    origin: THosOrigin;
    annotations?: IAnnotation[];
    coDrivers?: IIdEntity[];
    editDateTime?: string;
    verifyDateTime?: string;
    version?: string;
    location?: IAddressLookupData;
    parentId?: string;
    state?: string;
    sequence?: string;
    malfunction?: THosMalfunction;
    isIgnored?: boolean;
    isHidden?: boolean;
    isRequestedRemoval?: boolean;
    odometer?: number;
    engineHours?: number;
    distanceSinceValidCoordinates?: number;
    deferralStatus?: TDeferralStatus;
    deferralMinutes?: number;
    eventCheckSum?: string;
    userHosRuleSet?: IIdEntity;

    // client-side props
    modified?: IModifiedType;
    previousState?: IDutyStatusLog;
    fromServer?: boolean;
    _syncCounter?: number;

    placeholder?: boolean;
    distance?: number;
    requestedEdit?: DutyStatusLog;
    requestedAdd?: boolean;
    originalDriverBeforeEdit?: string;
    _address?: string;
    _isTempIoxLog?: boolean;
    _createdDate?: string;
    _awaitingTransition?: boolean;
    _originalDateTimeBeforeEdit?: string;
    _originalDistance?: number;
    _nextLogDateTime?: string;

    constructor(object?: DeepReadonly<IDutyStatusLog>) {
        this.id = object?.id || "";
        this.dateTime = object?.dateTime || "";
        this.driver = object?.driver && { id: object.driver.id } || { id: "" };
        this.device = object?.device && { id: object.device.id } || { id: "" };
        this.status = object?.status || "D";
        this.origin = object?.origin || "Manual";
        if (object) {
            this.instantiateOptionalProps(object);
            this.instantiateClientSideProps(object);
        }
    }

    getDate() {
        return toDateTime(this.dateTime);
    }

    getVerifyDate() {
        return !this.hasPendingRequest() && this.verifyDateTime ? new Date(this.verifyDateTime) : null;
    }

    isVerified() {
        return !this.hasPendingRequest() && this.getVerifyDate() !== null;
    }

    isVerifiedForDisplay(includePlaceHolders = false) {
        return !this.hasPendingRequest() && (this.isVerified() || !this.isNormalLog(includePlaceHolders) || this.isIntermediateLog());
    }

    isNoDevice() {
        let deviceId = this.getDeviceId();
        return !deviceId || deviceId === NODEVICEID;
    }

    hasPendingRequest() {
        return this.isRequestedAdd() || (!!this.requestedEdit && !this.isAwaitingTransition() && (this.requestedEdit.state === REQUESTED || this.requestedEdit.state === "Transitioning"));
    }

    hasPendingRequestForCurrentUser(currentUser: IUser | DeepReadonly<IUser> | null) {
        return this.hasPendingRequest() && (!currentUser || this.getDriverId() === currentUser.id || (this.requestedEdit && (this.requestedEdit.driver).id === currentUser.id));
    }

    verify() {
        this.verifyDateTime = getServerDate().toISOString();
    }

    unverify() {
        this.verifyDateTime = undefined;
    }

    addAnnotation(annotationObj: IAnnotation) {
        let allAnnotations = this.annotations || [];
        allAnnotations.push(annotationObj);
        this.annotations = allAnnotations;
    }

    getDeviceId() {
        return this.device ? this.device.id : undefined;
    }

    getDriverId () {
        return this.driver ? this.driver.id : undefined;
    }

    getCoDrivers() {
        return this.coDrivers || [];
    }

    isControlLog(excludeControlLogTypes: THosStatus[] = []) {
        let status = this.status;
        return status ? !(ALL_STANDARD_STATUSES.indexOf(status) > -1 || (excludeControlLogTypes.indexOf(status) > -1)) : false;
    }

    getControlLogBadge(): string {
        return CONTROL_LOG_BADGES[this.status] ?? "!";
    }

    isMalfunctionLog() {
        return MALFUNCTION_STATUSES.indexOf(this.status) > -1;
    }

    isSystemLog() {
        return SYSTEM_STATUSES.indexOf(this.status) > -1;
    }

    isEngineLog() {
        return ENGINE_STATUSES.indexOf(this.status) > -1;
    }

    isUneditableLog() {
        let uneditableTypes: THosStatus[] = ["INT_D", "PC", "PC_Exempted", "INT_PC", "YM", "WT"];
        return uneditableTypes.indexOf(this.status) > -1 || this.isAutomaticDriving();
    }

    isLoginLogoffLog() {
        let status = this.status;
        return status === "Login" || status === "Logoff";
    }

    isPositionComplianceLog() {
        return this.status === "PositioningCompliance";
    }

    isPositioningComplianceDiagnosticLog() {
        return this.isPositionComplianceLog() && this.isDiagnostic();
    }

    isDataTransferComplianceDiagnosticLog() {
        return this.status === "DataTransferCompliance" && this.malfunction === "Diagnostic";
    }

    isDataTransferComplianceMalfunctionLog() {
        return this.status === "DataTransferCompliance" && this.isMalfunction();
    }

    isUnidentifiedDrivingComplianceLog(){
        return this.status === "UnidentifiedDrivingCompliance";
    }

    isSystemClear() {
        if (!this.malfunction) {
            return false;
        }
        return !!this.malfunction.match(/Clear/gi);
    }

    isDTCSystemClearLog() {
        return this.status === "DataTransferCompliance" && this.isSystemClear();
    }

    isDTCLog() {
        return this.status === "DataTransferCompliance";
    }

    isDiagnostic() {
        if (!this.malfunction) {
            return false;
        }
        return !!this.malfunction.match(/Diagnostic/gi);
    }

    isMalfunction() {
        if (!this.malfunction) {
            return false;
        }
        return !!this.malfunction.match(/Malfunction/gi);
    }

    isTimingComplianceLog() {
        return this.status === "TimingCompliance";
    }

    isMissingElementComplianceLog() {
        return this.status === "MissingElementCompliance";
    }

    isPlaceholder() {
        return !!this.placeholder;
    }

    isAuthority() {
        return this.status === "Authority";
    }

    isUnidentified() {
        return !this.driver
               || this.driver.id === NOUSERID
               || (!!this.requestedEdit && this.requestedEdit.state === "Active" && this.requestedEdit.driver && this.requestedEdit.driver.id === NOUSERID);
    }

    isAutomaticLog() {
        return this.origin === "Automatic" || this.origin === "Unassigned";
    }

    isStandardLog(includeExemptions = false) {
        return (includeExemptions ? ALL_HOS_STATUSES : HOS_STANDARD_STATUSES).indexOf(this.status) > -1;
    }

    isIgnorableLog() {
        return this.driver && this.driver.id !== NOUSERID
            && (ALL_HOS_STATUSES.indexOf(this.status) > -1 || EXEMPTION_LOG_TYPES.indexOf(this.status) > -1);
    }

    isDrivingStatus() {
        let status = this.status;
        return status === "D" || status === "INT_D";
    }

    isAutomaticDriving() {
        return this.isDrivingStatus() && this.isAutomaticLog();
    }

    isManualDriving() {
        return this.status === "D" && (this.origin === "Manual" || this.origin === "OtherUser");
    }

    isCertifyLog() {
        return this.status === "Certify";
    }

    isOperatingZoneChange() {
        return OPERATING_ZONE_STATUSES.indexOf(this.status) > -1;
    }

    isAutomaticOn() {
        return this.status === "ON" && this.isAutomaticLog();
    }

    isIntermediateLog() {
        return this.status === "INT_PC" || this.status === "INT_D";
    }

    isPCLog() {
        return this.status === "PC" || this.status === "INT_PC" || this.status === "PC_Exempted";
    }

    isEnginePowerUpLog() {
        return this.status === "EnginePowerup" || this.status === "EnginePowerupPC";
    }

    isEngineShutDownLog() {
        return this.status === "EngineShutdown" || this.status === "EngineShutdownPC";
    }

    isValidLocation() {
        return (!!this.location?.location
            && this.location.location.x !== 0
            && this.location.location.y !== 0
            && (!this.malfunction || this.malfunction !== "Diagnostic"))
            || !!this.location?.address?.formattedAddress;
    }

    isMissingAddress() {
        return this.isNormalLog() && !this.isValidLocation() && !this._address && (this.isNoDevice() || this.malfunction === "Diagnostic" || this.malfunction === "Malfunction");
    }

    isPcYm() {
        return this.isPCLog() || this.isYmLog();
    }

    isDrivingWhileInExemptionRelatedLog() {
        return this.status === "DrivingWhileInExemption" || this.status === "DrivingStoppedWhileInExemption";
    }

    isSituationalDrivingClear() {
        return this.status === "SituationalDrivingClear";
    }

    isSituationalDrivingStatus() {
        const status = this.status;
        return this.isPcYm() || status === "WT";
    }

    isOnDutyStatus() {
        let onDutyStatuses = ["D", "ON", "YM"];
        return onDutyStatuses.indexOf(this.status) > -1;
    }

    isEwdLog() {
        return this.status === "Rest" || this.status === "Work" || this.status === "WorkExemption";
    }

    cleanUp(): void {
        Object.keys(this).forEach((key: string) => {
            if (typeof key === "string" && key.indexOf("_") === 0) {
                delete this[key as IDutyStatusLogType];
            }
        });
    }

    getDistance(): number {
        return this.distance || 0;
    }

    getPreviousState() {
        return this.previousState ? new DutyStatusLog(this.previousState) : null;
    }

    getRequestedVersion() {
        return this.requestedAdd ? this.version : (this.requestedEdit && this.requestedEdit.version || null);
    }

    getRequestChanges() {
        let edit = this.requestedEdit;
        let additionalFieldsToCheck = ["verifyDateTime"];

        if (!edit) {
            return null;
        }

        return extend({}, this.getChanges(Object.keys(edit) as IDutyStatusLogType[]), this.getChanges(additionalFieldsToCheck as IDutyStatusLogType[]));
    }

    hasAnnotations() {
        return !!this.annotations && this.annotations.length > 0;
    }

    getOriginDescription() {
        switch (this.origin) {
            case "Automatic":
                return "Automatic system log";
            case "Manual":
                return "Manual driver log";
            case "OtherUser":
                return "Other authenticated user";
            case "Unassigned":
                return "Unassigned driver log";
            default:
                return "-";
        }
    }

    isStandardStatusDriverLog() {
        return !this.isUnidentified()
            && !this.isRemoved()
            && !this.isIntermediateLog()
            && !this.isMalfunctionLog()
            && !this.hasPendingRequest();
    }

    isStandardStatusDriverLogForReport(currentUser: DeepReadonly<IUser> | null) {
        return !this.isUnidentified()
            && !this.isRemoved()
            && !this.isMalfunctionLog()
            && !this.isSystemLog()
            && (!this.hasPendingRequest() || (!this.isRequestedAdd() && currentUser && this.getDriverId() === currentUser?.id));
    }

    isGraphLog() {
        return this.isStandardStatusDriverLog()
            && !this.isControlLog()
            && !this.isLoginLogoffLog();
    }

    isDashboardLog() {
        return this.isStandardStatusDriverLog() && !this.isControlLog();
    }

    isHosProcessLog() {
        return this.isStandardStatusDriverLog() && !this.isPlaceholder() && !this.isAuthority();
    }

    isLogRequiredForDvirOnLog() {
        return (this.isStandardStatusDriverLog() && !this.isPlaceholder()) || this.isLoginLogoffLog();
    }

    isUnassignedPageLog() {
        return !this.isRemoved()
            && this.isUnidentified()
            && !this.hasPendingRequest()
            && (this.status === "D" || this.status === "ON");
    }

    isCycleChangeLog() {
        return CYCLE_STATUSES.indexOf(this.status) > -1;
    }

    isCycleOrZoneChangeLog() {
        return CYCLE_AND_OPERATING_ZONE_STATUSES.indexOf(this.status) > -1;
    }

    isOffDutyDeferral() {
        return this.status === "ExemptionOffDutyDeferral";
    }

    isEditableExemptionLog() {
        return this.status === "PC" || this.status === "YM";
    }

    isEmergencyResponseExemptionLog() {
        return EMERGENCY_RESPONSE_EXEMPTION_LOG_TYPES.indexOf(this.status) > -1;
    }

    isMainLogPageLog(
        currentUser: IUser | DeepReadonly<IUser> | null,
        includePlaceHolders = true,
        includeLoginLogoffLogs = false,
        includeEmergencyResponseExemptionLogs = false
    ) {
        // Guard before the pending-request fallback below, which would otherwise surface requested Emergency Response logs.
        if (!includeEmergencyResponseExemptionLogs && this.isEmergencyResponseExemptionLog()) {
            return false;
        }
        let extractControlLogs: THosStatus[] = [
            "AdverseWeather",
            "Exemption16H",
            "ExemptionOffDutyDeferral",
            "RailroadExemption",
            "ExemptionReducedRest"
        ];
        if (includeLoginLogoffLogs) {
            extractControlLogs.push("Login", "Logoff");
        }
        if (includeEmergencyResponseExemptionLogs) {
            extractControlLogs.push(...EMERGENCY_RESPONSE_EXEMPTION_LOG_TYPES);
        }
        return (this.isNormalLog(includePlaceHolders, includeLoginLogoffLogs, extractControlLogs) ||
        this.hasPendingRequestForCurrentUser(currentUser)) && !this.isIntermediateLog();
    }

    isNormalLog(
        includePlaceHolders = false,
        includeLoginLogoffLogs = false,
        extractControlLogs: THosStatus[] = [],
        includeRequestedAdds = false,
        includeIntermediateLogs = true
    ) {
        return this.isNormalLogImpl(includePlaceHolders, includeLoginLogoffLogs, extractControlLogs, includeRequestedAdds, includeIntermediateLogs);
    }

    isNormalLogIncludingControlLogs(includePlaceHolders = false, includeLoginLogoffLogs = false, includeRequestedAdds = false, includeIntermediateLogs = true) {
        return !this.isRemoved()
        && !this.isUnidentified()
        && (includePlaceHolders || !this.isPlaceholder())
        && (includeLoginLogoffLogs || !this.isLoginLogoffLog())
        && (includeRequestedAdds || !this.isRequestedAdd())
        && (includeIntermediateLogs || !this.isIntermediateLog());
    }

    isNormalLogImpl(
        includePlaceHolders = false,
        includeLoginLogoffLogs = false,
        extractControlLogs: THosStatus[] = [],
        includeRequestedAdds = false,
        includeIntermediateLogs = true
    ) {
        return this.isNormalLogIncludingControlLogs(includePlaceHolders, includeLoginLogoffLogs, includeRequestedAdds, includeIntermediateLogs)
            && !this.isControlLog(extractControlLogs);
    }

    isMotionStoppedLog() {
        return this.status === "MotionStopped";
    }

    isOnlyPcLog() {
        return this.status === "PC";
    }

    isYmLog() {
        return this.status === "YM";
    }

    isOnLog() {
        return this.status === "ON";
    }

    isEnginePowerupPcLog() {
        return this.status === "EnginePowerupPC";
    }

    isRegularDisplayLog() {
        return DISPLAY_STATUSES.indexOf(this.status) !== -1;
    }

    isAwaitingTransition() {
        return this._awaitingTransition;
    }

    clone() {
        return new DutyStatusLog(this);
    }

    isRemoved () {
        return this.modified === "Remove" || this.state === "Inactive" || this.state === "Rejected" || this.isRequestedRemoval === true;
    }

    private isRequestedAdd() {
        return this.state === REQUESTED;
    }

    private getChanges(fieldsCollection: IDutyStatusLogType[]) {
        let comparators = this.getComparatorsForRequestedEditCheck();
        let unSupportedProps = [
            "annotations", "coDrivers", "id", "location", "state",
            "editDateTime", "version", "sequence", "eventRecordStatus"
        ];
        let edit = this.requestedEdit;
        let changes: Record<string, any> = {};

        if (!edit) {
            return {};
        }

        for (let index = 0; index < fieldsCollection.length; index++) {
            let prop = fieldsCollection[index];
            let comparator = this.getComparators(prop as keyof IComparators) || comparators.default(prop);

            if (unSupportedProps.indexOf(prop) === -1 && !comparator(edit[prop])) {
                changes[prop as string] = edit[prop];
            }
        }

        return changes;
    }

    private getComparatorsForRequestedEditCheck () {
        return {
            default: (property: keyof(DutyStatusLog)) => {
                return (newValue: string) => newValue === this[property];
            },
            device: (newValue: IIdEntity | string) => {
                let deviceId = this.getDeviceId();
                if (!newValue || newValue === NODEVICEID) {
                    return !deviceId || deviceId === NODEVICEID;
                }
                if (typeof newValue === "string") {
                    return newValue === deviceId;
                }
                return newValue.id === deviceId;
            },
            driver: (newValue: IIdEntity | string) => {
                let driverId = this.getDriverId();
                if (!newValue || newValue === NOUSERID) {
                    return !driverId || driverId === NOUSERID;
                }
                if (typeof newValue === "string") {
                    return newValue === driverId;
                }
                return newValue.id === driverId;
            }
        };
    }

    private getComparators(key: keyof IComparators): (param: any) => any {
        const comparators = this.getComparatorsForRequestedEditCheck();
        return comparators[key];
    }

    private instantiateOptionalProps(object: DeepReadonly<IDutyStatusLog>) {
        if (object.annotations) {
            this.annotations = JSON.parse(JSON.stringify(object.annotations));
        }
        if (object.coDrivers) {
            this.coDrivers = JSON.parse(JSON.stringify(object.coDrivers));
        }
        this.editDateTime = object.editDateTime ? object.editDateTime : getServerDate().toISOString();
        if (object.verifyDateTime) {
            this.verifyDateTime = object.verifyDateTime;
        }
        if (object.version) {
            this.version = object.version;
        }
        if (object.location) {
            this.location = {};
            if (object.location.address) {
                this.location.address = { ...object.location.address };
            }
            if (object.location.location) {
                this.location.location = { ...object.location.location };
            }
        }
        if (object.parentId) {
            this.parentId = object.parentId;
        }
        if (object.state) {
            this.state = object.state;
        }
        if (object.sequence) {
            this.sequence = object.sequence;
        }
        this.malfunction = object.malfunction || "None";
        if (object.isIgnored !== undefined) {
            this.isIgnored = object.isIgnored;
        }
        if (object.isHidden !== undefined) {
            this.isHidden = object.isHidden;
        }
        if (object.isRequestedRemoval !== undefined) {
            this.isRequestedRemoval = object.isRequestedRemoval;
        }
        if (object.odometer !== undefined) {
            this.odometer = object.odometer;
        }
        if (object.engineHours !== undefined) {
            this.engineHours = object.engineHours;
        }
        if (object.distanceSinceValidCoordinates !== undefined) {
            this.distanceSinceValidCoordinates = object.distanceSinceValidCoordinates;
        }
        if (object.deferralMinutes !== undefined) {
            this.deferralMinutes = object.deferralMinutes;
        }
        if (object.deferralStatus) {
            this.deferralStatus = object.deferralStatus;
        }
        if (object.eventCheckSum) {
            this.eventCheckSum = object.eventCheckSum;
        }
        if (object.originalDriverBeforeEdit) {
            this.originalDriverBeforeEdit = object.originalDriverBeforeEdit;
        }
        if (object.userHosRuleSet) {
            this.userHosRuleSet = object.userHosRuleSet;
        }
    }

    private instantiateClientSideProps(object: DeepReadonly<IDutyStatusLog>) {
        if (object.modified) {
            this.modified = object.modified;
        }
        if (object.previousState) {
            this.previousState = new DutyStatusLog(object.previousState);
        }
        if (object.fromServer) {
            this.fromServer = object.fromServer;
        }
        this._syncCounter = object._syncCounter ? object._syncCounter : 5; // Default value for sync retry

        if (object.placeholder) {
            this.placeholder = object.placeholder;
        }
        if (object.distance !== undefined) {
            this.distance = object.distance;
        }
        if (object.requestedEdit) {
            this.requestedEdit = new DutyStatusLog(object.requestedEdit);
        }
        if (object.requestedAdd) {
            this.requestedAdd = object.requestedAdd;
        }
        if (object._isTempIoxLog) {
            this._isTempIoxLog = object._isTempIoxLog;
        }
        if (object._createdDate) {
            this._createdDate = object._createdDate;
        }
        if (object._address) {
            this._address = object._address;
        }
        if (object._awaitingTransition) {
            this._awaitingTransition = object._awaitingTransition;
        }
        if (object._originalDateTimeBeforeEdit) {
            this._originalDateTimeBeforeEdit = object._originalDateTimeBeforeEdit;
        }
        if (object._originalDistance !== undefined) {
            this._originalDistance = object._originalDistance;
        }
        if (object._nextLogDateTime !== undefined) {
            this._nextLogDateTime = object._nextLogDateTime;
        }
    }
}